import { useForm } from "@inertiajs/react";
import { adminAccountsPath } from "@travily/routes";
import type { FormEvent, ReactNode } from "react";
import { AdminRoutedModal } from "@/components/AdminRoutedModal";
import { useImageUploadField } from "@/hooks/use-image-upload-field";
import { type AccountTypeLabel, NewView, type Visibility } from "./new.view";

interface FormData {
  profileable_type: AccountTypeLabel;
  name: string;
  contact_name: string;
  contact_email: string;
  visibility: Visibility;
  avatar_signed_id: string;
  cover_image_signed_id: string;
}

const INITIAL_DATA: FormData = {
  profileable_type: "Community",
  name: "",
  contact_name: "",
  contact_email: "",
  visibility: "hidden",
  avatar_signed_id: "",
  cover_image_signed_id: "",
};

const AdminAccountNewPage = () => {
  const form = useForm<FormData>(INITIAL_DATA);
  const { data, setData, post, processing, transform } = form;
  const avatarField = useImageUploadField({
    onUploaded: (signedId) => setData("avatar_signed_id", signedId),
    onRemoved: () => setData("avatar_signed_id", ""),
  });
  const coverField = useImageUploadField({
    onUploaded: (signedId) => setData("cover_image_signed_id", signedId),
    onRemoved: () => setData("cover_image_signed_id", ""),
  });

  transform((formData) => ({ account: formData }));

  const needsProfileName = data.profileable_type !== "Individual";
  const canSubmit =
    data.contact_name.trim().length > 0 &&
    data.contact_email.trim().length > 0 &&
    (!needsProfileName || data.name.trim().length > 0);

  const handleAvatarDrop = (files: FileList) => {
    const file = files[0];
    if (file) avatarField.onFileSelect(file);
  };

  const handleCoverDrop = (files: FileList) => {
    const file = files[0];
    if (file) coverField.onFileSelect(file);
  };

  return (
    <AdminRoutedModal>
      {({ close }) => {
        const handleSubmit = (e: FormEvent) => {
          e.preventDefault();
          if (!canSubmit) return;
          post(adminAccountsPath(), { preserveState: true, onSuccess: () => close() });
        };

        return (
          <NewView
            accountType={data.profileable_type}
            onAccountTypeChange={(type) => setData("profileable_type", type)}
            name={data.name}
            onNameChange={(value) => setData("name", value)}
            contactName={data.contact_name}
            onContactNameChange={(value) => setData("contact_name", value)}
            contactEmail={data.contact_email}
            onContactEmailChange={(value) => setData("contact_email", value)}
            visibility={data.visibility}
            onVisibilityChange={(value) => setData("visibility", value)}
            avatarPreview={avatarField.previewUrl}
            onAvatarDrop={handleAvatarDrop}
            onAvatarRemove={avatarField.onRemove}
            coverPreview={coverField.previewUrl}
            onCoverDrop={handleCoverDrop}
            onCoverRemove={coverField.onRemove}
            canSubmit={canSubmit}
            processing={processing}
            onSubmit={handleSubmit}
            onClose={close}
          />
        );
      }}
    </AdminRoutedModal>
  );
};

AdminAccountNewPage.layout = (page: ReactNode) => page;

export default AdminAccountNewPage;
